package com.flowyride.feature.planner.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.foundation.layout.size
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.flowyride.core.designsystem.component.ExpandToggle
import com.flowyride.core.domain.model.Place
import com.flowyride.feature.planner.PlannerAction
import com.flowyride.feature.planner.PlannerMode
import com.flowyride.feature.planner.PlannerUiState
import com.flowyride.feature.planner.RouteUiState
import com.flowyride.feature.planner.resources.Res
import com.flowyride.feature.planner.resources.collapse
import com.flowyride.feature.planner.resources.expand
import com.flowyride.feature.planner.resources.locating_rider
import com.flowyride.feature.planner.resources.options_title
import org.jetbrains.compose.resources.stringResource

/** Always visible part of the sheet: the loop to generate, or the route and its start button. */
@Composable
internal fun SheetHeader(state: PlannerUiState, onAction: (PlannerAction) -> Unit, modifier: Modifier = Modifier) {
    val departure = state.stops.first()
    if (state.mode == PlannerMode.LOOP && state.imported == null) {
        LoopHeader(state, onAction, modifier)
    } else if (departure.followsRider && departure.selected == null) {
        Row(modifier, verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            CircularProgressIndicator(Modifier.size(20.dp), strokeWidth = 2.dp)
            Text(stringResource(Res.string.locating_rider), style = MaterialTheme.typography.bodyMedium)
        }
    } else {
        RouteStatusHeader(state.route, onAction, state.canNavigate, modifier)
    }
}

/** The open sheet: stops (or loop settings, or the imported line), the route's details and actions, then the options. */
@Composable
internal fun SheetBody(
    state: PlannerUiState,
    onAction: (PlannerAction) -> Unit,
    onSaveFavorite: () -> Unit,
    onSavePlace: (Place) -> Unit,
    modifier: Modifier = Modifier,
) {
    val ready = state.route as? RouteUiState.Ready
    Column(modifier, verticalArrangement = Arrangement.spacedBy(16.dp)) {
        val imported = state.imported
        when {
            imported != null -> ImportedRideSection(imported, onAction)
            state.mode == PlannerMode.LOOP -> LoopSection(state, onAction)
            else -> {
                StopsSection(state, onAction, onSavePlace)
                RerollLoopButton(state, onAction)
            }
        }
        if (ready != null && state.mode == PlannerMode.ROUTE) {
            RouteActions(ready, onAction, onSaveFavorite, state.canNavigate)
            RouteDetails(ready, onAction)
        }
        HorizontalDivider()
        CollapsibleOptions(state, onAction, rideEditable = imported == null)
    }
}

/** Rarely changed: folded under one line. */
@Composable
private fun CollapsibleOptions(state: PlannerUiState, onAction: (PlannerAction) -> Unit, rideEditable: Boolean) {
    var open by rememberSaveable { mutableStateOf(false) }
    Column {
        Row(Modifier.fillMaxWidth().clickable { open = !open }, verticalAlignment = Alignment.CenterVertically) {
            Text(stringResource(Res.string.options_title), Modifier.weight(1f), style = MaterialTheme.typography.titleSmall)
            ExpandToggle(
                expanded = open,
                onToggle = { open = !open },
                contentDescription = stringResource(if (open) Res.string.collapse else Res.string.expand),
            )
        }
        AnimatedVisibility(open) {
            RideOptionsSection(state, onAction, rideEditable = rideEditable)
        }
    }
}
